import React, {

  createContext,

  useCallback,

  useContext,

  useEffect,

  useMemo,

  useRef,

  useState,

} from "react";

import { useNavigate } from "react-router";

import { InteractiveTutorial } from "../Components/Tutorial/TutorialModal";

import type { TutorialStep, TutorialStepId } from "../Constants/tutorialSteps";

import { PATH } from "../Constants/paths";

import { isTutorialDemoAccountId } from "../Constants/tutorialDemoData";

import { TUTORIAL_AMC_CLIENT_ID } from "../Constants/tutorialScenario";

import { USER_ROLE } from "../Types/user";

import { useWorkspaceClient } from "./WorkspaceClientContext";

import { useClientCapabilities } from "../hooks/useClientCapabilities";

import {

  hasCompletedTutorial,

  markTutorialCompleted,

  resolveTutorialSteps,

} from "../utils/tutorialHelpers";

import {

  setTutorialFixtureActive,

  isTutorialFixtureActive,

} from "../utils/tutorialFixtureState";

import {

  captureClientIdBeforeTour,

  clearTourClientBackup,

  isTutorialWorkspaceClientId,

  readStoredWorkspaceClientId,

  readTourClientBackup,

  resolveClientIdForTourRestore,

  writeStoredWorkspaceClientId,

} from "../utils/workspaceClientHelpers";



type TutorialContextValue = {

  isOpen: boolean;

  /** True while the interactive App Guide is visible. */

  isTourActive: boolean;

  currentStepId: TutorialStepId | null;

  steps: TutorialStep[];

  openTutorial: () => void;

  closeTutorial: () => void;

};



const defaultValue: TutorialContextValue = {

  isOpen: false,

  isTourActive: false,

  currentStepId: null,

  steps: [],

  openTutorial: () => {},

  closeTutorial: () => {},

};



const TutorialContext = createContext<TutorialContextValue>(defaultValue);



type TutorialProviderProps = {

  children: React.ReactNode;

  enabled: boolean;

  userId: string | null | undefined;

  userRole: USER_ROLE | null | undefined;

};



export const TutorialProvider: React.FC<TutorialProviderProps> = ({

  children,

  enabled,

  userId,

  userRole,

}) => {

  const [isOpen, setIsOpen] = useState(false);

  const [currentStep, setCurrentStep] = useState<TutorialStep | null>(null);

  const autoStartedRef = useRef(false);

  /** Client selected before the tour forced Summitlane; `undefined` = nothing captured. */

  const clientBeforeTourRef = useRef<string | null | undefined>(undefined);

  const workspace = useWorkspaceClient();

  const navigate = useNavigate();

  const {
    capabilities,
    isAdminWithoutClient,
  } = useClientCapabilities();

  const steps = useMemo(
    () =>
      enabled
        ? resolveTutorialSteps(userRole, {
            capabilities,
            ungated: isAdminWithoutClient,
          })
        : [],
    [enabled, userRole, capabilities, isAdminWithoutClient],
  );



  const canShowTutorial = enabled && Boolean(userId) && steps.length > 0;

  const isTourActive = isOpen && canShowTutorial;



  const capturePreTourClient = useCallback(() => {

    if (!userId || !workspace.canSwitchClient) return;

    if (clientBeforeTourRef.current !== undefined) return;

    clientBeforeTourRef.current = captureClientIdBeforeTour({

      userId,

      currentClientId: workspace.selectedClientId,

    });

  }, [userId, workspace.canSwitchClient, workspace.selectedClientId]);



  const openTutorial = useCallback(() => {

    if (!canShowTutorial) return;

    capturePreTourClient();

    setIsOpen(true);

  }, [canShowTutorial, capturePreTourClient]);



  const endTourCleanup = useCallback(() => {

    // Drop fixtures before any page effects re-read client/display state.

    setTutorialFixtureActive(false);



    if (userId && workspace.canSwitchClient) {

      const backup =

        clientBeforeTourRef.current !== undefined

          ? clientBeforeTourRef.current

          : readTourClientBackup(userId);

      const stored = readStoredWorkspaceClientId(userId);

      const availableIds = workspace.clients.map((client) => String(client.id));

      const restoreTo = resolveClientIdForTourRestore({

        backup,

        stored,

        availableClientIds: availableIds,

      });



      clientBeforeTourRef.current = undefined;

      clearTourClientBackup(userId);



      // Never leave Summitlane selected or cached in workspace storage.

      if (

        isTutorialWorkspaceClientId(stored) ||

        isTutorialWorkspaceClientId(workspace.selectedClientId)

      ) {

        writeStoredWorkspaceClientId(userId, restoreTo);

      }



      if (workspace.selectedClientId !== restoreTo) {

        workspace.setSelectedClientId(restoreTo);

      } else if (isTutorialWorkspaceClientId(stored)) {

        writeStoredWorkspaceClientId(userId, restoreTo);

      }

    } else {

      clientBeforeTourRef.current = undefined;

      if (userId) clearTourClientBackup(userId);

    }



    // Leave tutorial demo account routes so fixture account state can't linger.

    const accountMatch = window.location.pathname.match(/^\/account\/([^/]+)/);

    const accountId = accountMatch?.[1];

    if (accountId && isTutorialDemoAccountId(accountId)) {

      navigate(PATH.ALL_ACCOUNTS, { replace: true });

    }



    setIsOpen(false);

    setCurrentStep(null);

  }, [

    navigate,

    userId,

    workspace.canSwitchClient,

    workspace.clients,

    workspace.selectedClientId,

    workspace.setSelectedClientId,

  ]);



  const closeTutorial = useCallback(() => {

    // Dismissing counts as seen, otherwise the guide re-opens on every page
    // load. It stays reachable from the App Guide nav button.

    if (userId) {

      markTutorialCompleted(userId);

    }

    endTourCleanup();

  }, [endTourCleanup, userId]);



  const completeTutorial = useCallback(() => {

    if (userId) {

      markTutorialCompleted(userId);

    }

    endTourCleanup();

  }, [endTourCleanup, userId]);



  const handleStepChange = useCallback((step: TutorialStep) => {

    setCurrentStep(step);

  }, []);



  useEffect(() => {

    if (!canShowTutorial || !userId || autoStartedRef.current) return;

    if (hasCompletedTutorial(userId)) return;



    autoStartedRef.current = true;

    const timer = window.setTimeout(() => {

      capturePreTourClient();

      setIsOpen(true);

    }, 700);

    return () => window.clearTimeout(timer);

  }, [canShowTutorial, userId, capturePreTourClient]);



  useEffect(() => {

    if (!enabled) {

      endTourCleanup();

    }

    // Only react to `enabled` flipping off — endTourCleanup identity changes often.

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [enabled]);



  // Sync during render so child useEffects (e.g. hotlist fetch) see fixtures on the same commit.

  if (isTutorialFixtureActive() !== isTourActive) {

    setTutorialFixtureActive(isTourActive);

  }



  useEffect(() => {

    return () => {

      setTutorialFixtureActive(false);

      if (userId) clearTourClientBackup(userId);

    };

  }, [userId]);



  // Force Summitlane while the tour is open — without writing it to workspace storage.

  useEffect(() => {

    if (!isTourActive || !workspace.canSwitchClient) return;

    capturePreTourClient();

    if (workspace.selectedClientId !== TUTORIAL_AMC_CLIENT_ID) {

      workspace.setSelectedClientId(TUTORIAL_AMC_CLIENT_ID, { persist: false });

    }

  }, [

    capturePreTourClient,

    isTourActive,

    workspace.canSwitchClient,

    workspace.selectedClientId,

    workspace.setSelectedClientId,

  ]);



  const value = useMemo(

    (): TutorialContextValue => ({

      isOpen,

      isTourActive,

      currentStepId: currentStep?.id ?? null,

      steps,

      openTutorial,

      closeTutorial,

    }),

    [closeTutorial, currentStep?.id, isOpen, isTourActive, openTutorial, steps],

  );



  return (

    <TutorialContext.Provider value={value}>

      {children}

      {canShowTutorial && isOpen ? (

        <InteractiveTutorial

          steps={steps}

          userRole={userRole!}

          onComplete={completeTutorial}

          onStepChange={handleStepChange}

        />

      ) : null}

    </TutorialContext.Provider>

  );

};



export function useTutorial(): TutorialContextValue {

  return useContext(TutorialContext);

}


